import { useEffect, useRef, useState } from 'react';

export function useFaceCamera(onCapture: (image: string) => Promise<boolean>) {
  const video = useRef<HTMLVideoElement>(null);
  const [attempt, setAttempt] = useState(0);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    let active = true;
    mounted.current = true;
    let stream: MediaStream | undefined;
    setReady(false); setError('');
    const stop = () => { stream?.getTracks().forEach((track) => track.stop()); };
    const hidden = () => {
      if (document.hidden) { stop(); setReady(false); setError('המצלמה הופסקה. יש להפעיל אותה שוב'); }
    };
    document.addEventListener('visibilitychange', hidden);
    if (!navigator.mediaDevices?.getUserMedia) setError('נדרשת מצלמה בדפדפן תומך ובחיבור מאובטח');
    else void navigator.mediaDevices.getUserMedia({ audio: false,
      video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } }).then((result) => {
      stream = result;
      if (!active || document.hidden) { stop(); return; }
      if (video.current) video.current.srcObject = result;
    }).catch((failure: unknown) => {
      if (!active) return;
      setError(failure instanceof DOMException && failure.name === 'NotAllowedError'
        ? 'הגישה למצלמה נחסמה. יש לאפשר מצלמה בהגדרות האתר'
        : 'לא ניתן להפעיל את המצלמה. בדקו שהיא מחוברת ואינה בשימוש ביישום אחר');
    });
    return () => { active = false; mounted.current = false; stop(); document.removeEventListener('visibilitychange', hidden); };
  }, [attempt]);

  const capture = async () => {
    if (lock.current || !ready || !video.current) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const source = video.current;
      if (!source.videoWidth || !source.videoHeight) throw new Error('טרם התקבלה תמונה מהמצלמה');
      const canvas = document.createElement('canvas');
      const scale = Math.min(1, 640 / Math.max(source.videoWidth, source.videoHeight));
      canvas.width = Math.round(source.videoWidth * scale); canvas.height = Math.round(source.videoHeight * scale);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('לא ניתן לצלם בדפדפן הזה');
      context.drawImage(source, 0, 0, canvas.width, canvas.height);
      const image = canvas.toDataURL('image/jpeg', 0.85);
      context.clearRect(0, 0, canvas.width, canvas.height); canvas.width = 0; canvas.height = 0;
      const accepted = await onCapture(image);
      if (mounted.current && !accepted) setError('האימות לא הושלם. אפשר לצלם שוב');
    } catch (failure) { if (mounted.current) setError(failure instanceof Error ? failure.message : 'הצילום נכשל'); }
    finally { lock.current = false; if (mounted.current) setBusy(false); }
  };
  return { video, ready, busy, error, capture, onReady: () => setReady(true), retry: () => setAttempt((previous) => previous + 1) };
}